import "./SearchForm.css";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ResponsiveTextField from "components/styled/ResponsiveTextField";
import PropTypes from "prop-types";
import React, { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { UserContext } from "utils/UserContext";

function SearchForm(props) {
  const { setAlbumsLoading, setAlbums, setAlbumTracks, searchFormRef } = props;

  const {
    setValue,
    handleSubmit,
    control,
    resetField,
    formState: { errors, isDirty, isSubmitted },
  } = useForm({
    defaultValues: {
      albumSearch: "",
    },
  });

  const { setSearched, searchQuery, setSearchQuery } = useContext(UserContext);

  const mainTheme = useTheme();

  const widthBelowSm = useMediaQuery(mainTheme.breakpoints.down("sm"));

  const onSubmit = (data) => {
    setAlbumsLoading(true);
    setAlbums([]);
    setAlbumTracks([]);
    setSearchQuery(data.albumSearch);
    window.sessionStorage.setItem(
      "SearchQuery",
      JSON.stringify(data.albumSearch)
    );
    setSearched(true);
  };

  useEffect(() => {
    if (searchQuery.length >= 1) {
      setValue("albumSearch", searchQuery);
    }
  }, [searchQuery]);

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: "0 20px",
        textAlign: "center",
      }}
      onSubmit={handleSubmit(onSubmit)}
      ref={searchFormRef}
    >
      <Grid container alignItems="center" direction="column" spacing={2}>
        <Grid item>
          <Typography
            component="h1"
            variant="directions"
            sx={{
              padding: "0rem",
              fontSize: "1.25rem",
              fontWeight: "bold",
            }}
          >
            What project do you want to score?
          </Typography>
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          justifyContent="center"
          spacing={2}
        >
          <Grid item>
            <Controller
              control={control}
              name="albumSearch"
              rules={{
                required: "Enter an artist or album",
              }}
              render={({ field }) => (
                <FormControl>
                  <InputLabel
                    disableAnimation
                    htmlFor="album-search-input"
                    sx={{
                      color: "transparent",
                    }}
                  >
                    Search an artist or album
                  </InputLabel>
                  <ResponsiveTextField
                    {...field}
                    variant="outlined"
                    id="album-search-input"
                    type="text"
                    placeholder={
                      widthBelowSm
                        ? "Artist or album"
                        : "Search an artist or album"
                    }
                    error={!!errors.albumSearch}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear input"
                            disabled={
                              !isDirty ||
                              (isSubmitted && searchQuery.length < 1)
                            }
                            onClick={() => {
                              resetField("albumSearch");
                            }}
                            edge="end"
                          >
                            <ClearIcon
                              sx={{
                                color:
                                  !isDirty ||
                                  (isSubmitted && searchQuery.length < 1)
                                    ? "transparent"
                                    : "#000",
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              )}
            />
            <Typography variant="inherit" color="textSecondary">
              {errors.albumSearch?.message}
            </Typography>
          </Grid>

          <Grid item>
            {!widthBelowSm ? (
              <Button
                disabled={!isDirty || (isSubmitted && searchQuery.length < 1)}
                sx={{
                  bgcolor: mainTheme.palette.darkBluePrimary.main,
                  padding: "0.3rem 1.5rem",
                  borderRadius: "1.2rem",
                  "&.Mui-disabled": {
                    bgcolor: mainTheme.palette.darkGrayPrimary.main,
                  },
                }}
                type="submit"
              >
                Search
              </Button>
            ) : (
              <IconButton
                type="submit"
                aria-label="search"
                disabled={!isDirty || (isSubmitted && searchQuery.length < 1)}
                sx={{
                  bgcolor: mainTheme.palette.darkBluePrimary.main,
                  padding: "0.5rem",
                  borderRadius: "50%",
                  "&.Mui-disabled": {
                    bgcolor: mainTheme.palette.darkGrayPrimary.main,
                  },
                }}
              >
                <SearchIcon
                  sx={{
                    color: isDirty && mainTheme.palette.whitePrimary.main,
                  }}
                />
              </IconButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

SearchForm.propTypes = {
  setAlbumsLoading: PropTypes.func.isRequired,
  setAlbums: PropTypes.func.isRequired,
  setAlbumTracks: PropTypes.func.isRequired,
  searchFormRef: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    PropTypes.func,
  ]).isRequired,
};

export default SearchForm;
